<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { PaymentDocument } from '../services/payment-document.service'

const props = defineProps<{
  document: PaymentDocument | null
  title: string
  type: 'terms' | 'privacy' | 'other'
  canEdit: boolean
}>()

const emit = defineEmits<{
  add: []
  edit: [document: PaymentDocument]
  delete: [document: PaymentDocument]
  viewHistory: [type: 'terms' | 'privacy']
}>()

const { t, locale } = useI18n()

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString(({ pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE', da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT', hr: 'hr-HR', fi: 'fi-FI' }[locale.value] || 'en-US'), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<template>
  <div class="mb-4 pb-4 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
    <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      <div class="flex-1">
        <h3 class="text-base font-medium text-gray-900">{{ title }}</h3>

        <div v-if="!document" class="mt-1">
          <p class="text-sm text-gray-500">{{ t('paymentDocumentCard.noDocument') }}</p>
        </div>

        <div v-else class="mt-1 space-y-1">
          <p class="text-sm text-gray-700 font-medium">{{ document.title }}</p>
          <p v-if="document.name" class="text-sm text-gray-600">{{ document.name }}</p>
          <div class="flex items-center gap-4 text-xs text-gray-500">
            <span>{{ t('paymentDocumentCard.version') }} {{ document.version }}</span>
            <span>{{ t('paymentDocumentCard.lastUpdated') }} {{ formatDate(document.updated_at) }}</span>
          </div>
        </div>
      </div>

      <div class="flex gap-2">
        <button
          v-if="!document && canEdit"
          @click="emit('add')"
          class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 transition-colors"
        >
          {{ t('paymentDocumentCard.addDocument') }}
        </button>

        <template v-if="document && canEdit">
          <button
            @click="emit('edit', document)"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 transition-colors"
          >
            {{ t('common.edit') }}
          </button>
          <button
            v-if="type === 'terms' || type === 'privacy'"
            @click="emit('viewHistory', type)"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 transition-colors"
            :title="t('paymentDocumentCard.historyTooltip')"
          >
            {{ t('paymentDocumentCard.history') }}
          </button>
          <button
            v-if="type !== 'terms' && type !== 'privacy'"
            @click="emit('delete', document)"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 transition-colors"
          >
            {{ t('common.delete') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
